import '@/polyfills';
import { DarkTheme, DefaultTheme, ThemeProvider as NavigationThemeProvider } from '@react-navigation/native';
import { Stack } from 'expo-router';
import { StatusBar } from 'expo-status-bar';
import { useEffect, useCallback } from 'react';
import { View } from 'react-native';
import 'react-native-reanimated';
import { GestureHandlerRootView } from 'react-native-gesture-handler';
import { SafeAreaProvider } from 'react-native-safe-area-context';
import { BottomSheetModalProvider } from '@gorhom/bottom-sheet';
import * as SplashScreen from 'expo-splash-screen';
import { useFonts } from 'expo-font';
import * as Notifications from 'expo-notifications';

import { useColorScheme } from '@/hooks/use-color-scheme';
import { useLocaleAutoPopulate } from '@/hooks/use-locale-auto-populate';
import { SupabaseProvider } from '@/lib/supabase';
import { ThemeProvider } from '@/lib/theme';
import { UserDeviceProvider, StorageInitializer } from '@/lib/storage';
import { missedNotificationManager } from '@/lib/services/MissedNotificationManager';
import { FONT_COMFORTAA, FONT_NOTO_SANS } from '@/lib/theme/FontManager';
import { AppErrorBoundary } from '@/components/ui/AppErrorBoundary';
import { ToastProviderWithViewport } from '@/components/ui/molecules/toast';

// Keep splash screen visible while fonts load
SplashScreen.preventAutoHideAsync();

// Configure notification handler
Notifications.setNotificationHandler({
  handleNotification: async () => ({
    shouldShowBanner: true,
    shouldShowList: true,
    shouldPlaySound: true,
    shouldSetBadge: false,
  }),
});

export const unstable_settings = {
  anchor: '(tabs)',
};

function RootLayoutContent() {
  const colorScheme = useColorScheme();

  useLocaleAutoPopulate();

  useEffect(() => {
    // Initialize missed notification manager
    missedNotificationManager.initialize();

    return () => {
      missedNotificationManager.cleanup();
    };
  }, []);

  return (
    <NavigationThemeProvider value={colorScheme === 'dark' ? DarkTheme : DefaultTheme}>
      <StorageInitializer />
      <Stack screenOptions={{ headerShown: false }}>
        <Stack.Screen name="(tabs)" />
        <Stack.Screen name="sign-in" />
        <Stack.Screen name="ai-summaries" />
        <Stack.Screen name="notification-settings" />
        <Stack.Screen name="edit-profile" />
        <Stack.Screen name="family-member/[id]" />
        <Stack.Screen name="family-member/create" />
        <Stack.Screen name="route-planner" />
        <Stack.Screen name="confirm-delete" />
        <Stack.Screen name="modal" options={{ headerShown: true, presentation: 'modal', title: 'Modal' }} />
      </Stack>
      <StatusBar style="auto" />
    </NavigationThemeProvider>
  );
}

export default function RootLayout() {
  const [fontsLoaded, fontError] = useFonts({
    [FONT_COMFORTAA]: require('../assets/fonts/Comfortaa/Comfortaa-VariableFont_wght.ttf'),
    [FONT_NOTO_SANS]: require('../assets/fonts/Noto Sans/NotoSans-VariableFont_wdth,wght.ttf'),
  });

  const onLayoutRootView = useCallback(async () => {
    if (fontsLoaded || fontError) {
      await SplashScreen.hideAsync();
    }
  }, [fontsLoaded, fontError]);

  if (!fontsLoaded && !fontError) {
    return null;
  }

  return (
    <View style={{ flex: 1 }} onLayout={onLayoutRootView}>
      <GestureHandlerRootView style={{ flex: 1 }}>
        <SafeAreaProvider>
          <SupabaseProvider>
            <UserDeviceProvider>
              <ThemeProvider>
                <BottomSheetModalProvider>
                  <AppErrorBoundary>
                    <ToastProviderWithViewport>
                      <RootLayoutContent />
                    </ToastProviderWithViewport>
                  </AppErrorBoundary>
                </BottomSheetModalProvider>
              </ThemeProvider>
            </UserDeviceProvider>
          </SupabaseProvider>
        </SafeAreaProvider>
      </GestureHandlerRootView>
    </View>
  );
}